import * as THREE from 'three';
import { registerShot, look } from '../core/Shots.js';
import { makeRng } from '../core/Rand.js';
import {
  sharedUniforms, registerSceneMaterials, registerMaterial, patchReport, CASCADE_COUNT,
} from './lightshaders/inject.js';
import { VOL_VERT, VOL_FRAG, VOL_COMPOSITE_FRAG } from './lightshaders/volumetric.glsl.js';
import { createEnv } from './Env.js';
import { TOD, UNIT_SCALE, EXPOSURE_KEY, displayKeyFor, keyLuminanceFor } from './Sky.js';

/**
 * Lighting — the light rig. Owner: lighting.
 *
 *  - 4-cascade PSSM sun shadows rendered into a single packed-depth atlas,
 *    stabilised with a sphere fit + texel snap so nothing swims, sampled with a
 *    PCSS blocker search so contact is razor sharp and distant shadow edges go
 *    soft. (three's own shadow path cannot do this: PCFShadowMap binds the map
 *    as a sampler2DShadow, which makes the blocker search impossible.)
 *  - Sun (warm key) + sky fill (cool) + ground bounce (albedo tinted).
 *  - Every practical from ctx.level.lightHints, inverse-square, colour graded by
 *    fixture type, with a hard shadow-caster budget.
 *  - Half-res raymarched sun shafts published on ctx.lighting.volumetricTexture.
 */

const DEG = Math.PI / 180;
const WHITE = new THREE.Color(1, 1, 1);

/** Planckian locus -> linear sRGB, normalised to unit luminance. */
export function kelvinToLinear(kelvin, out = new THREE.Color()) {
  const t = THREE.MathUtils.clamp(kelvin, 1000, 20000) / 100;
  let r, g, b;
  if (t <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(t) - 161.1195681661;
    b = t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
    b = 255;
  }
  const s = (v) => {
    const c = THREE.MathUtils.clamp(v, 0, 255) / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const lr = s(r), lg = s(g), lb = s(b);
  const lum = Math.max(1e-4, 0.2126 * lr + 0.7152 * lg + 0.0722 * lb);
  return out.setRGB(lr / lum, lg / lum, lb / lum);
}

/* ------------------------------------------------------------------ *
 * Cascaded shadow maps
 * ------------------------------------------------------------------ */

class CascadeAtlas {
  constructor(renderer, { tile = 2048, cascades = CASCADE_COUNT, lambda = 0.72 } = {}) {
    this.tile = tile;
    this.cascades = cascades;
    this.lambda = lambda;
    this.tilesPerRow = 2;
    this.near = 0.6;
    this.far = 160;
    this.margin = 70;

    this.target = new THREE.WebGLRenderTarget(tile * this.tilesPerRow, tile * this.tilesPerRow, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      depthBuffer: true,
      stencilBuffer: false,
      colorSpace: THREE.NoColorSpace,
    });
    this.target.texture.name = 'csmAtlas';

    this.depthMaterial = new THREE.MeshDepthMaterial({
      depthPacking: THREE.RGBADepthPacking,
      side: THREE.DoubleSide,
      blending: THREE.NoBlending,
    });

    this.cams = [];
    for (let i = 0; i < cascades; i++) {
      const c = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 100);
      c.name = `csmCascade${i}`;
      this.cams.push(c);
    }

    this.splits = new Array(cascades).fill(1);
    this._bias = new THREE.Matrix4().set(
      0.5, 0, 0, 0.5,
      0, 0.5, 0, 0.5,
      0, 0, 0.5, 0.5,
      0, 0, 0, 1,
    );
    this._fwd = new THREE.Vector3();
    this._center = new THREE.Vector3();
    this._v = new THREE.Vector3();
    this._upY = new THREE.Vector3(0, 1, 0);
    this._upZ = new THREE.Vector3(0, 0, 1);
    this._hidden = [];
    this._prevViewport = new THREE.Vector4();
    this._prevScissor = new THREE.Vector4();
    this._lastCentre = Array.from({ length: cascades }, () => new THREE.Vector3(1e9, 1e9, 1e9));
    this._lastSun = new THREE.Vector3();
    this._dirtyCascade = new Array(cascades).fill(true);
    this._forceAll = false;
    this.renderer = renderer;
  }

  computeSplits() {
    const { near, far, cascades, lambda } = this;
    for (let i = 1; i <= cascades; i++) {
      const si = i / cascades;
      const log = near * Math.pow(far / near, si);
      const uni = near + (far - near) * si;
      this.splits[i - 1] = lambda * log + (1 - lambda) * uni;
    }
    this.splits[cascades - 1] = far;
  }

  update(camera, sunDir) {
    this.computeSplits();
    camera.getWorldDirection(this._fwd);
    const sunMoved = this._lastSun.distanceToSquared(sunDir) > 1e-10;
    this._lastSun.copy(sunDir);

    const ty = Math.tan(camera.fov * 0.5 * DEG);
    const tx = ty * camera.aspect;
    const a2 = tx * tx + ty * ty;

    for (let i = 0; i < this.cascades; i++) {
      const n = i === 0 ? this.near : this.splits[i - 1];
      const f = this.splits[i];

      // Sphere fit — rotation invariant, so the cascade extent never changes
      // as the camera turns. That is what stops shadow edges from crawling.
      let d = (n + f) * (a2 + 1) * 0.5;
      if (d > f) d = f;
      const r = Math.sqrt(f * f * a2 + (f - d) * (f - d));

      this._center.copy(camera.position).addScaledVector(this._fwd, d);

      const cam = this.cams[i];
      const up = Math.abs(sunDir.y) > 0.985 ? this._upZ : this._upY;
      const back = r + this.margin;

      cam.up.copy(up);
      cam.position.copy(this._center).addScaledVector(sunDir, back);
      cam.lookAt(this._center);
      cam.left = -r; cam.right = r; cam.top = r; cam.bottom = -r;
      cam.near = 0;
      cam.far = 2 * r + this.margin;
      cam.updateProjectionMatrix();
      cam.updateMatrixWorld(true);

      // Snap the cascade centre to the shadow texel grid.
      const texel = (2 * r) / this.tile;
      this._v.copy(this._center).applyMatrix4(cam.matrixWorldInverse);
      this._v.x = Math.round(this._v.x / texel) * texel;
      this._v.y = Math.round(this._v.y / texel) * texel;
      this._v.applyMatrix4(cam.matrixWorld);

      cam.position.copy(this._v).addScaledVector(sunDir, back);
      cam.lookAt(this._v);
      cam.updateMatrixWorld(true);

      // Distant cascades only cover static geometry; if the snapped centre and
      // the sun are unchanged the tile is bit-identical, so skip the render.
      const moved = this._lastCentre[i].distanceToSquared(this._v) > 1e-8;
      this._dirtyCascade[i] = i < 2 || moved || sunMoved || this._forceAll;
      this._lastCentre[i].copy(this._v);

      const m = sharedUniforms.aaaCascadeMatrix.value[i];
      m.copy(this._bias).multiply(cam.projectionMatrix).multiply(cam.matrixWorldInverse);

      sharedUniforms.aaaCascadeInfo.value[i].set(this.splits[i], texel, cam.far - cam.near, i);
    }

    sharedUniforms.aaaShadowFar.value = this.far;
    sharedUniforms.aaaShadowParams.value.x = this.tilesPerRow;
    sharedUniforms.aaaShadowParams.value.y = this.tile;
    sharedUniforms.aaaShadowAtlas.value = this.target.texture;
  }

  render(renderer, scene, allCasters) {
    let any = false;
    for (let i = 0; i < this.cascades; i++) any = any || this._dirtyCascade[i];
    if (!any) return;

    const hidden = this._hidden;
    hidden.length = 0;
    scene.traverse((o) => {
      if (!o.visible) return;
      if (o.userData.aaaHelper) { o.visible = false; hidden.push(o); return; }
      if (o.isMesh && !allCasters && o.castShadow !== true) { o.visible = false; hidden.push(o); }
    });

    const prevBg = scene.background;
    const prevOverride = scene.overrideMaterial;
    const prevFog = scene.fog;
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    const prevShadowEnabled = renderer.shadowMap.enabled;
    // setViewport/setScissor mutate renderer state that outlives the render
    // target switch — without restoring them the main pass draws into a
    // 2048x2048 corner of the canvas and the whole frame goes black.
    renderer.getViewport(this._prevViewport);
    renderer.getScissor(this._prevScissor);
    const prevScissorTest = renderer.getScissorTest();

    // Clearing to white = "no blocker at max depth"; packDepthToRGBA(1) is 1,1,1,1.
    // Done through scene.background so we never touch renderer.setClearColor.
    scene.background = WHITE;
    scene.overrideMaterial = this.depthMaterial;
    scene.fog = null;
    renderer.shadowMap.enabled = false;
    renderer.autoClear = true;
    renderer.setRenderTarget(this.target);
    renderer.setScissorTest(true);

    for (let i = 0; i < this.cascades; i++) {
      if (!this._dirtyCascade[i]) continue;
      const x = (i % this.tilesPerRow) * this.tile;
      const y = Math.floor(i / this.tilesPerRow) * this.tile;
      renderer.setViewport(x, y, this.tile, this.tile);
      renderer.setScissor(x, y, this.tile, this.tile);
      renderer.render(scene, this.cams[i]);
    }

    renderer.setViewport(this._prevViewport);
    renderer.setScissor(this._prevScissor);
    renderer.setScissorTest(prevScissorTest);
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    renderer.shadowMap.enabled = prevShadowEnabled;
    scene.background = prevBg;
    scene.overrideMaterial = prevOverride;
    scene.fog = prevFog;

    for (const o of hidden) o.visible = true;
    hidden.length = 0;
  }

  dispose() {
    this.target.dispose();
    this.depthMaterial.dispose();
  }
}

/* ------------------------------------------------------------------ */

export function createLightingSystem() {
  const rng = makeRng(0x11ee77);

  const lights = [];
  const fixtures = [];
  let ctxRef = null;
  let env = null;
  let csm = null;

  /* Practical-lamp budget. three evaluates EVERY visible point light in every
     fragment (no clustering), so 42 practicals in a day scene were 84 % of the
     measured frame on an M4 (543 ms → 89 ms with them off). The budget keeps
     the K most present lamps — nearest and brightest to the camera — and hides
     the rest. Note a change in the visible count re-links every program (three
     bakes NUM_POINT_LIGHTS into the shader), so this is applied on preset
     switches and on a slow clock, never per frame. */
  let lampBudget = Infinity;
  let lampClock = 0;
  const lampPos = new THREE.Vector3();

  function applyLampBudget(ctx) {
    if (!fixtures.length || !ctx?.camera) return;
    if (!Number.isFinite(lampBudget)) {
      for (const f of fixtures) f.light.visible = true;
      return;
    }
    const cam = ctx.camera.position;
    const scored = fixtures.map((f) => {
      const d2 = f.light.getWorldPosition(lampPos).distanceToSquared(cam);
      return [f, f.light.intensity / (1 + d2)];
    });
    scored.sort((a, b) => b[1] - a[1]);
    scored.forEach(([f], i) => { f.light.visible = i < lampBudget; });
    // At budget 0 the FX pool lights (kept visible at intensity 0 precisely to
    // hold the program cache stable) go too — the recompile is already being
    // paid by the preset switch, and 4 idle point lights are ~8 ms on an M4.
    const fxOn = lampBudget > 0;
    ctx.scene.traverse((o) => { if (o.isPointLight && o.userData?.fx) o.visible = fxOn; });
  }

  let sun = null;
  let skyFill = null;
  let bounce = null;

  let volRT = null;
  let volDepthRT = null;
  let volScene = null;
  let volCamera = null;
  let volMat = null;
  let volQuad = null;
  let compositeQuad = null;
  let compositeMat = null;
  let blueNoise = null;
  let volScale = 0.5;
  let volWidth = 2, volHeight = 2;

  let frames = 0;
  let shotStateUntil = -1;
  let envDirty = true;
  let allCasters = false;

  const quality = {
    shadows: true,
    volumetrics: true,
    volumetricSteps: 40,
    probes: true,
    irradianceVolume: true,
    shadowCasterBudget: 3,
    // Reusing the post pipeline's prepass depth would save a scene pass, but
    // its contents are not guaranteed at the point in the frame we sample it
    // and a stale/blank read floods the frame with shafts. Off until the
    // render team confirms a stable hand-off.
    useSharedDepth: false,
  };

  const grading = {
    /**
     * Cool sky fill. Raised from 0.07: it is the only *unoccluded* cool term in
     * the rig and it is what stops a shadow reading warmer than the key. The
     * review measured shadowed ground at linear R/B 2.07 against a sky of 0.69 —
     * the shadow was being carried by the warm ground-bounce light below, not by
     * the sky above it.
     */
    skyFillScale: 0.125,
    /**
     * Directional ground bounce. Halved from 0.20 for the same reason: this is
     * a crude warm up-light with no occlusion, and now that the irradiance
     * volume carries real one-bounce GI with the right colour in the right
     * places, most of what this used to stand in for is double counted.
     */
    bounceScale: 0.10,
    practicalScale: 1.0,
    volumetricIntensity: 1.0,
    exposureBias: 0,      // 0 = leave the post pipeline's auto-exposure alone

    /**
     * Golden-hour key grade. The review reads the sunlit wall at linear R/B 1.72
     * and calls it 4500 K; a golden-hour key should read 2500-3500 K.
     *
     * This cannot be fixed in the atmosphere: `ATMO.betaMext` is a single scalar,
     * so Mie extinction in this model is spectrally flat and turbidity dims the
     * sun without reddening it (measured: R/B 1.449 at turbidity 4 and 1.409 at
     * turbidity 13). Raising ozone makes the sky *less* blue, which is backwards.
     * Nor is dropping the sun an option — FINDINGS F1b is exactly why it was
     * raised to 34 deg.
     *
     * So the warmth is an explicit grade, and it is applied as a lerp between
     * two unit-luminance colours. Luminance is linear in RGB, so the graded
     * colour is still exactly unit luminance: `sun.intensity` stays the physical
     * `sunLux * UNIT_SCALE`, `derived.exposure` is untouched, and the F1
     * acceptance test is unaffected. Only the hue moves.
     */
    keyKelvin: 3000,
    keyGrade: 0.55,

    /**
     * The cool half of the same decision. Measured at TOD.golden the physical
     * sky illuminance is already (0.697, 1.027, 1.622) normalised — R/B 0.43,
     * i.e. roughly 13 000 K — so the sky *fill* was never the warm term. What
     * was warm is the one-bounce ambient: the irradiance volume's dominant
     * contribution in a shaded pocket is light that has come off a sunlit sand
     * facade at albedo (0.42, 0.36, 0.26), and that lands on the shadowed
     * ground at linear R/B 2.10 against a sunlit 2.52.
     *
     * `ambientCool` pulls the *bounce* term's chromaticity toward the sky's by
     * this fraction, at constant luminance. Physically it stands for the sky
     * the bouncing surface itself sees and the second bounce this model does
     * not trace; practically it is the art director's blue grade, applied where
     * it actually moves the measurement instead of on a term that is already
     * cooler than the target.
     *
     * `skyKelvin` is the floor the fill is held at: if a time of day produces a
     * sky *warmer* than this (dawn, dusk, heavy turbidity) it is graded back
     * toward it, so the key/fill split never collapses. At golden hour the
     * physical sky is bluer than 9000 K and is left alone.
     */
    skyKelvin: 9000,
    skyGrade: 0.85,
    ambientCool: 0.34,
  };
  const KEY_TARGET = new THREE.Color();
  const SKY_TARGET = new THREE.Color();
  /** Unit-luminance multiplier: physical sky chromaticity -> graded. Read by Env. */
  const skyTint = new THREE.Color(1, 1, 1);
  let keyKelvinCached = -1;
  let skyKelvinCached = -1;

  /* ---------------- sun + fill rig ---------------- */

  function buildRig(ctx) {
    const sky = ctx.sky;

    sun = new THREE.DirectionalLight(0xffffff, 1);
    sun.name = 'sun';
    sun.castShadow = false;            // we run our own cascades
    sun.target.position.set(0, 0, 0);
    ctx.scene.add(sun);
    ctx.scene.add(sun.target);

    skyFill = new THREE.HemisphereLight(0x9dc4ff, 0x6b5540, 0);
    skyFill.name = 'skyFill';
    ctx.scene.add(skyFill);

    bounce = new THREE.DirectionalLight(0xffffff, 0);
    bounce.name = 'groundBounce';
    bounce.castShadow = false;
    bounce.position.set(0, -1, 0);
    bounce.target.position.set(0, 0, 0);
    ctx.scene.add(bounce);
    ctx.scene.add(bounce.target);

    lights.push(sun, skyFill, bounce);
  }

  function syncRig(ctx) {
    const sky = ctx.sky;
    if (!sky) return;
    const d = sky.derived;

    // Physical sun hue, graded toward the golden-hour target. Both endpoints are
    // unit luminance, so the lerp is too — the intensity below stays physical.
    if (keyKelvinCached !== grading.keyKelvin) {
      keyKelvinCached = grading.keyKelvin;
      kelvinToLinear(grading.keyKelvin, KEY_TARGET);
    }
    sun.color.copy(sky.sunColor).lerp(KEY_TARGET, THREE.MathUtils.clamp(grading.keyGrade, 0, 1));
    sun.intensity = d.sunLux * UNIT_SCALE;
    sun.position.copy(sky.sunDirection).multiplyScalar(400).add(ctx.camera.position);
    sun.target.position.copy(ctx.camera.position);
    sun.target.updateMatrixWorld();

    // Cool fill. Both endpoints are unit luminance so the lerp is too — only
    // the hue moves, `skyFill.intensity` below stays the physical figure.
    if (skyKelvinCached !== grading.skyKelvin) {
      skyKelvinCached = grading.skyKelvin;
      kelvinToLinear(grading.skyKelvin, SKY_TARGET);
    }
    skyFill.color.copy(sky.skyColor);
    // Only ever cool: at golden hour the physical sky is bluer than the target
    // and grading toward 9000 K would warm it, which is backwards.
    if (skyFill.color.r / Math.max(1e-4, skyFill.color.b) > SKY_TARGET.r / SKY_TARGET.b) {
      skyFill.color.lerp(SKY_TARGET, THREE.MathUtils.clamp(grading.skyGrade, 0, 1));
    }
    // The tint that turns the physical sky chromaticity into the graded one.
    // Env applies it to the irradiance volume's bounce term so shaded ground
    // picks up the same grade the fill does. Unit luminance by construction.
    skyTint.setRGB(
      skyFill.color.r / Math.max(1e-4, sky.skyColor.r),
      skyFill.color.g / Math.max(1e-4, sky.skyColor.g),
      skyFill.color.b / Math.max(1e-4, sky.skyColor.b),
    );
    {
      const l = Math.max(1e-4, 0.2126 * skyTint.r + 0.7152 * skyTint.g + 0.0722 * skyTint.b);
      skyTint.multiplyScalar(1 / l);
    }
    skyFill.groundColor.copy(sky.groundBounceColor);
    skyFill.intensity = d.skyLux * grading.skyFillScale * UNIT_SCALE;

    const bLum = 0.2126 * d.groundBounce.x + 0.7152 * d.groundBounce.y + 0.0722 * d.groundBounce.z;
    bounce.color.copy(sky.groundBounceColor);
    bounce.intensity = bLum * grading.bounceScale * UNIT_SCALE;
    // Bounce comes up off the ground, tilted away from the sun.
    bounce.position.set(-sky.sunDirection.x * 0.35, -1, -sky.sunDirection.z * 0.35)
      .normalize().multiplyScalar(200).add(ctx.camera.position);
    bounce.target.position.copy(ctx.camera.position);
    bounce.target.updateMatrixWorld();

    sharedUniforms.aaaSunDirection.value.copy(sky.sunDirection);

    /**
     * Aerial perspective carries the same grade. The haze's in-scatter is a
     * sky-cube sample, and at low elevation that sample is the cloud deck —
     * pale and, near the sun, warm. Tinting it with the graded sky chromaticity
     * is what makes distance read cool against a warm key instead of making the
     * whole frame one sandy hue.
     */
    sky.fogParams.skyTint.setRGB(
      THREE.MathUtils.lerp(1, skyTint.r, 0.6),
      THREE.MathUtils.lerp(1, skyTint.g, 0.6),
      THREE.MathUtils.lerp(1, skyTint.b, 0.6),
    );
    sharedUniforms.aaaFogSkyTint.value.copy(sky.fogParams.skyTint);
  }

  /* ---------------- practicals ---------------- */

  /**
   * Fixture profiles. `intensity` on a light hint is a relative dial (the level
   * team authors 0.5-2.0), so the real photometric value is
   * `hint.intensity * profile.candela`, in candela, before the scene unit
   * conversion. Colour temperatures are pinned per fixture class so the frame
   * never lands on one hue: tungsten practicals 2700 K against a 4200 K key and
   * a 9000 K sky fill.
   */
  /**
   * Two changes from the first pass, both from the review:
   *
   *  - every `fill` profile ran at `decay: 1.25`. The review found "the wall is
   *    lit evenly top to bottom — no inverse-square falloff from a 2 m source",
   *    and 1.25 is precisely that: at 2 m vs 4 m a 1.25 decay drops 58 %, a
   *    physical 2 decay drops 75 %. Everything is decay 2 now.
   *  - output roughly doubled across the board. The old numbers were the low
   *    end of physical (95 cd ~ a 100 W incandescent) and the review found
   *    practicals that "emit nothing" — which they physically do next to 1300 lx
   *    of skylight. These are still real photometric figures: 210 cd is a bright
   *    domestic fixture, 1500 cd a modest streetlight.
   */
  const FIXTURES = {
    // by hint.role first — the level tells us exactly what the fixture is
    streetlight: { kelvin: 2150, candela: 1500, decay: 2, shadow: true, bulb: 0.11 },
    stall_lamp: { kelvin: 2600, candela: 260, decay: 2, shadow: true, bulb: 0.06 },
    shop_fill: { kelvin: 2900, candela: 150, decay: 2, fill: true },
    backroom_fill: { kelvin: 3900, candela: 95, decay: 2, fill: true },
    upstairs_fill: { kelvin: 2850, candela: 130, decay: 2, fill: true },
    stairwell_fill: { kelvin: 4300, candela: 85, decay: 2, fill: true },
    passage_fill: { kelvin: 8000, candela: 70, decay: 2, fill: true },

    // by hint.type
    room: { kelvin: 3200, candela: 110, decay: 2, fill: true },
    point: { kelvin: 2700, candela: 210, decay: 2, bulb: 0.055 },
    tungsten: { kelvin: 2700, candela: 210, decay: 2, bulb: 0.055 },
    bulb: { kelvin: 2600, candela: 180, decay: 2, bulb: 0.05 },
    lamp: { kelvin: 2900, candela: 290, decay: 2, bulb: 0.07 },
    signage: { kelvin: 2300, candela: 120, decay: 2, bulb: 0.05 },
    sign: { kelvin: 2300, candela: 120, decay: 2, bulb: 0.05 },
    neon: { kelvin: 7000, candela: 130, decay: 2, bulb: 0.05 },
    fluorescent: { kelvin: 4600, candela: 270, decay: 2, bulb: 0.09 },
    interior: { kelvin: 2750, candela: 230, decay: 2, bulb: 0.06 },
    emergency: { kelvin: 1800, candela: 150, decay: 2, bulb: 0.05 },
    fire: { kelvin: 1900, candela: 130, decay: 2, bulb: 0.06 },
    spot: { kelvin: 3200, candela: 820, decay: 2, spot: true, angle: 54, penumbra: 0.42, shadow: true },
  };

  const PROBE_TYPES = ['probe', 'reflection', 'room', 'interior'];

  function profileFor(h) {
    const role = (h.role || '').toLowerCase();
    if (FIXTURES[role]) return { key: role, ...FIXTURES[role] };
    const type = (h.type || 'point').toLowerCase();
    if (FIXTURES[type]) return { key: type, ...FIXTURES[type] };
    return { key: 'tungsten', ...FIXTURES.tungsten };
  }

  function buildPracticals(ctx) {
    for (const l of fixtures) {
      const i = lights.indexOf(l.light);
      if (i >= 0) lights.splice(i, 1);
      l.light.removeFromParent();
      l.light.target?.removeFromParent();
      l.mesh?.removeFromParent();
      l.mesh?.geometry.dispose();
      l.mesh?.material.dispose();
      l.light.dispose?.();
    }
    fixtures.length = 0;

    const hints = ctx.level?.lightHints;
    if (!Array.isArray(hints) || hints.length === 0) return;

    // Rank by how much each light actually matters, then spend the shadow budget.
    const scored = [];
    for (const h of hints) {
      if (!h || !h.position) continue;
      const type = (h.type || 'point').toLowerCase();
      if (type === 'probe' || type === 'reflection') continue;
      const preset = profileFor(h);
      const radius = h.radius ?? 9;
      const candela = (h.intensity ?? 1) * preset.candela * grading.practicalScale;
      scored.push({ h, type, preset, radius, candela, score: candela / Math.max(1, radius) });
    }
    scored.sort((a, b) => b.score - a.score);

    let shadowsLeft = quality.shadowCasterBudget;
    const spawn = ctx.level?.spawnPoints?.[0];

    for (const s of scored) {
      const { h, preset } = s;
      const color = new THREE.Color();
      if (h.color !== undefined && h.color !== null) {
        // Level colours are authoring hues; keep the hue, drop the brightness so
        // `intensity` stays a real candela figure.
        color.set(h.color);
        const lum = Math.max(1e-4, 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b);
        color.multiplyScalar(1 / lum);
      } else {
        kelvinToLinear(preset.kelvin, color);
      }

      const intensity = s.candela * UNIT_SCALE;
      let light;
      if (preset.spot) {
        light = new THREE.SpotLight(0xffffff, intensity, s.radius, preset.angle * 0.5 * DEG, preset.penumbra, preset.decay);
        light.position.copy(h.position);
        light.target.position.set(h.position.x, h.position.y - 4, h.position.z);
        ctx.scene.add(light.target);
      } else {
        light = new THREE.PointLight(0xffffff, intensity, s.radius, preset.decay);
        light.position.copy(h.position);
      }
      light.color.copy(color);
      light.name = `practical_${preset.key}`;
      light.userData.role = h.role || preset.key;

      // Only the few fixtures that actually shape a silhouette get a shadow map.
      const nearPlayer = spawn ? h.position.distanceTo(spawn) < 60 : true;
      if (preset.shadow && shadowsLeft > 0 && nearPlayer) {
        light.castShadow = true;
        light.shadow.mapSize.set(1024, 1024);
        light.shadow.bias = -0.0014;
        light.shadow.normalBias = 0.035;
        light.shadow.radius = 3;
        light.shadow.camera.near = 0.12;
        light.shadow.camera.far = Math.max(4, s.radius);
        shadowsLeft--;
      }

      ctx.scene.add(light);
      lights.push(light);

      // A small emissive bulb so the fixture itself blooms in the post pipeline.
      let mesh = null;
      if (preset.bulb && h.emissive !== false) {
        const mat = new THREE.MeshBasicMaterial({ toneMapped: false, fog: false });
        // Radiance of a sphere emitting `candela`: I / (pi * r^2), in scene units.
        const r = h.bulbRadius ?? preset.bulb;
        const radiance = s.candela / (Math.PI * r * r) * UNIT_SCALE;
        mat.color.copy(color).multiplyScalar(Math.min(48, radiance));
        mesh = new THREE.Mesh(new THREE.SphereGeometry(r, 8, 6), mat);
        mesh.position.copy(h.position);
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        mesh.userData.noCollide = true;
        mesh.name = 'practicalBulb';
        ctx.scene.add(mesh);
      }

      fixtures.push({ light, mesh, hint: h, type: preset.key, fill: !!preset.fill });
    }
  }

  /* ---------------- volumetrics ---------------- */

  function makeBlueNoise(ctx) {
    const supplied = ctx.materials?.blueNoise;
    if (supplied && supplied.isTexture) return supplied;

    // Roberts' R2 low-discrepancy dither — blue-noise-like spectrum, exact and
    // deterministic, no asset needed.
    const S = 64;
    const data = new Uint8Array(S * S * 4);
    const g = 1.32471795724474602596;
    const a1 = 1 / g, a2 = 1 / (g * g);
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const v = (a1 * x + a2 * y + 0.5 * rng() * 0) % 1;
        const i = (y * S + x) * 4;
        const b = Math.floor(v * 255);
        data[i] = b; data[i + 1] = b; data[i + 2] = b; data[i + 3] = 255;
      }
    }
    const tex = new THREE.DataTexture(data, S, S, THREE.RGBAFormat);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = tex.magFilter = THREE.NearestFilter;
    tex.colorSpace = THREE.NoColorSpace;
    tex.needsUpdate = true;
    return tex;
  }

  function buildVolumetrics(ctx) {
    blueNoise = makeBlueNoise(ctx);

    const rtOpts = {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      generateMipmaps: false,
      depthBuffer: false,
      colorSpace: THREE.NoColorSpace,
    };
    volRT = new THREE.WebGLRenderTarget(2, 2, rtOpts);
    volRT.texture.name = 'volumetricShafts';

    volDepthRT = new THREE.WebGLRenderTarget(2, 2, {
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      depthBuffer: true,
      colorSpace: THREE.NoColorSpace,
    });

    volMat = new THREE.ShaderMaterial({
      defines: { AAA_CASCADES: CASCADE_COUNT },
      uniforms: {
        uDepth: { value: volDepthRT.texture },
        uHwDepth: { value: 0 },
        uReversedDepth: { value: 0 },
        uBlueNoise: { value: blueNoise },
        uNoiseScale: { value: new THREE.Vector2(1, 1) },
        uFrameJitter: { value: 0 },
        uInvProjection: { value: new THREE.Matrix4() },
        uInvView: { value: new THREE.Matrix4() },
        uCameraPos: { value: new THREE.Vector3() },
        uCameraRange: { value: new THREE.Vector2(0.02, 2000) },
        aaaShadowAtlas: sharedUniforms.aaaShadowAtlas,
        aaaCascadeMatrix: sharedUniforms.aaaCascadeMatrix,
        aaaCascadeInfo: sharedUniforms.aaaCascadeInfo,
        aaaShadowParams: sharedUniforms.aaaShadowParams,
        uSunDirection: sharedUniforms.aaaSunDirection,
        uSunIlluminance: { value: new THREE.Vector3() },
        uAmbientRadiance: { value: new THREE.Vector3() },
        uVolParams: { value: new THREE.Vector4(1.0, 1 / 11, 0.0, 0.70) },
        /* Clean outdoor air. sigma_s = 9e-5 /m is roughly Rayleigh plus a light
           haze; over the 65 m this march covers it adds ~0.06 scene units when
           you look straight into the sun and essentially nothing anywhere else,
           so it cannot re-create the F1 veil. Every bit of shaft-forming
           density comes from uIndoorDust instead. */
        uScatter: { value: 0.9e-4 },
        // Single-scattering albedo 0.92 — dust is a strong scatterer and a weak
        // absorber. At 0.78 the extinction ate the far end of a dusty room.
        uExtinction: { value: 0.98e-4 },
        /* Dust multiplier where the irradiance volume says there is no sky.
           Measured, not guessed: with the room's own surfaces sitting around
           0.13 scene units, a beam has to contribute ~0.05 over its 4 m of lit
           path to read at all, which needs sigma_s ~ 4e-3 /m. 70x on top of
           9e-5, times the ~0.64 height falloff at head height, lands there.
           sigma_t comes out at 5e-3, i.e. ~780 m visibility — a hazy room, not
           a smoky one: across a 20 m hall it costs 10 % transmittance, which
           reads as depth rather than as fog. 15x, the first attempt, moved the
           beam by 5 % of the floor value and was invisible.
           Measured on the shot: 70x -> 400x moves the beam region by +0.031
           mean luminance, so 70x on its own is worth ~0.005 and still does not
           read. 220x puts sigma_s at 1.25e-2 /m at head height, a beam at ~28 %
           over the floor it lands on, and 21 % transmittance loss across a 15 m
           hall — depth, not fog. */
        uIndoorDust: { value: 220 },
        aaaIvSky: sharedUniforms.aaaIvSky,
        aaaIvMin: sharedUniforms.aaaIvMin,
        aaaIvInvSize: sharedUniforms.aaaIvInvSize,
        aaaIvTexelInset: sharedUniforms.aaaIvTexelInset,
        uMaxDistance: { value: 65 },
        uSteps: { value: quality.volumetricSteps },
      },
      vertexShader: VOL_VERT,
      fragmentShader: VOL_FRAG,
      depthTest: false,
      depthWrite: false,
    });

    volScene = new THREE.Scene();
    volCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    volQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), volMat);
    volQuad.frustumCulled = false;
    volScene.add(volQuad);

    compositeMat = new THREE.ShaderMaterial({
      uniforms: {
        uVolumetric: { value: volRT.texture },
        uTexel: { value: new THREE.Vector2(1 / 960, 1 / 540) },
        uIntensity: { value: grading.volumetricIntensity },
      },
      vertexShader: VOL_VERT,
      fragmentShader: VOL_COMPOSITE_FRAG,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    });
    compositeQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), compositeMat);
    compositeQuad.frustumCulled = false;
    compositeQuad.renderOrder = 9000;
    compositeQuad.name = 'volumetricComposite';
    compositeQuad.userData.aaaHelper = true;
    compositeQuad.userData.noCollide = true;
    ctx.scene.add(compositeQuad);
  }

  function resizeVolumetrics(ctx) {
    const w = Math.max(2, Math.floor(ctx.engine.drawWidth * volScale));
    const h = Math.max(2, Math.floor(ctx.engine.drawHeight * volScale));
    if (w === volWidth && h === volHeight) return;
    volWidth = w; volHeight = h;
    volRT.setSize(w, h);
    volDepthRT.setSize(w, h);
    volMat.uniforms.uNoiseScale.value.set(w / 64, h / 64);
    compositeMat.uniforms.uTexel.value.set(1 / w, 1 / h);
  }

  const _hiddenVol = [];

  function renderVolumetrics(ctx) {
    if (!quality.volumetrics || !volRT) return;
    resizeVolumetrics(ctx);

    const renderer = ctx.renderer;
    const scene = ctx.scene;
    const camera = ctx.camera;
    const sky = ctx.sky;
    const prevTarget = renderer.getRenderTarget();

    // Scene depth. The post pipeline already renders a depth prepass every
    // frame; reusing its buffer saves a whole extra scene pass and costs only
    // one frame of latency on the shafts, which nobody can see.
    const shared = quality.useSharedDepth ? ctx.render?.passes?.prepass?.depthTexture : null;
    if (shared) {
      volMat.uniforms.uDepth.value = shared;
      volMat.uniforms.uHwDepth.value = 1;
      volMat.uniforms.uReversedDepth.value = renderer.reversedDepthBuffer ? 1 : 0;
    } else {
      volMat.uniforms.uDepth.value = volDepthRT.texture;
      volMat.uniforms.uHwDepth.value = 0;
      volMat.uniforms.uReversedDepth.value = 0;

      _hiddenVol.length = 0;
      scene.traverse((o) => {
        if (o.visible && o.userData.aaaHelper) { o.visible = false; _hiddenVol.push(o); }
      });

      const prevBg = scene.background;
      const prevOverride = scene.overrideMaterial;
      const prevFog = scene.fog;
      const prevShadowEnabled = renderer.shadowMap.enabled;

      scene.background = WHITE;
      scene.overrideMaterial = csm.depthMaterial;
      scene.fog = null;
      renderer.shadowMap.enabled = false;
      renderer.setRenderTarget(volDepthRT);
      renderer.render(scene, camera);

      scene.background = prevBg;
      scene.overrideMaterial = prevOverride;
      scene.fog = prevFog;
      renderer.shadowMap.enabled = prevShadowEnabled;
      for (const o of _hiddenVol) o.visible = true;
      _hiddenVol.length = 0;
    }

    // --- march
    const u = volMat.uniforms;
    u.uInvProjection.value.copy(camera.projectionMatrixInverse);
    u.uInvView.value.copy(camera.matrixWorld);
    u.uCameraPos.value.copy(camera.position);
    u.uCameraRange.value.set(camera.near, camera.far);
    u.uFrameJitter.value = (ctx.time.frame % 8) * 0.125;
    if (sky) {
      u.uSunIlluminance.value.copy(sky.derived.sunIlluminance).multiplyScalar(UNIT_SCALE);
      u.uAmbientRadiance.value.copy(sky.derived.avgSkyRadiance).multiplyScalar(0.55 * UNIT_SCALE);
    }
    u.uSteps.value = quality.volumetricSteps;

    renderer.setRenderTarget(volRT);
    renderer.render(volScene, volCamera);
    renderer.setRenderTarget(prevTarget);
  }

  /* ---------------- exposure ---------------- */

  let exposureApplied = -1;
  let keySmoothed = -1;
  const _irr = { up: 0, down: 0, vis: 1, valid: false };
  const _sunProbe = new THREE.Vector3();
  const _lastExpCam = new THREE.Vector3(1e9, 1e9, 1e9);

  /**
   * Time constant of the anchor's indoor/outdoor term, in seconds.
   *
   * This is the "slow term" and it is the only thing in the exposure chain that
   * is allowed to follow the camera. It has to be slow enough that walking past
   * a doorway does not pump the frame, and fast enough that actually walking
   * *through* one has settled before the player has taken three steps. 0.55 s is
   * about two thirds of the histogram's own up-adaptation (2.6 /s), so the
   * anchor leads and the residual barely has to move.
   */
  const EXPOSURE_TAU = 0.55;

  /**
   * Is the sun visible from the camera? One ray, because the term it gates is
   * already softened by the volume's cosine-weighted sky visibility — a shadow
   * edge crossing moves `sunVis` from 1 to 0 but `vis` barely changes, and the
   * 0.55 s smoothing below turns the step into a ramp.
   */
  function sunVisibility(ctx) {
    const phys = ctx.physics;
    if (!phys || typeof phys.raycast !== 'function') return 1;
    _sunProbe.copy(ctx.sky.sunDirection);
    const mask = phys.MASK ? (phys.MASK.WORLD | phys.MASK.PROP) : undefined;
    return phys.raycast(ctx.camera.position, _sunProbe, 220, mask) ? 0 : 1;
  }

  /**
   * The scene luminance the camera is standing in, in scene units — the anchor's
   * whole reason for existing.
   *
   *   irradiance = direct sun (gated by an actual shadow ray and by how much sky
   *                this point can see)
   *              + the baked irradiance volume, which already carries sky
   *                through openings, one bounce, and the interreflection series
   *
   * Every term comes from the *lighting model*, never from the rendered frame.
   * That is deliberate and it is the difference between a measurement and a
   * tautology: `tools/exposure-check.mjs` reads the ratio of this prediction to
   * what the pipeline actually rendered, so an anchor derived from the frame
   * would make the tool report 1.000 on everything and prove nothing — which is
   * exactly the failure mode that retired the last two acceptance tests.
   */
  function localKeyLuminance(ctx) {
    const sky = ctx.sky;
    const d = sky.derived;
    const cosSun = Math.max(0, sky.sunDirection.y);

    let amb = null;
    if (env && typeof env.sampleIrradiance === 'function') {
      const s = env.sampleIrradiance(ctx.camera.position, _irr);
      if (s.valid) amb = s;
    }

    /**
     * How much haze the camera is standing in. Aerial perspective is an
     * outdoor phenomenon: a shuttered room accumulates it over its own 8 m of
     * depth otherwise, and 8 m of daylight in-scatter added to an interior is
     * how FINDINGS F1's milky veil looked. Floored at 0.3 so a doorway or a
     * skylight still reads as depth rather than snapping off.
     */
    sharedUniforms.aaaFogIndoor.value = amb
      ? 0.3 + 0.7 * THREE.MathUtils.clamp(amb.vis, 0, 1)
      : 1.0;

    let irradiance;
    if (amb) {
      const sunVis = sunVisibility(ctx) * THREE.MathUtils.clamp(amb.vis, 0, 1);
      irradiance = d.sunLux * cosSun * UNIT_SCALE * sunVis + amb.up + amb.down;
    } else {
      // No volume baked yet (first frames, or a level with no navBounds):
      // fall back to the open-sky analytic value, which is what the anchor's
      // reference is made of anyway.
      irradiance = d.openIrradiance;
    }
    return keyLuminanceFor(irradiance);
  }

  /**
   * Publish the physically derived exposure anchor and hand it to the post
   * pipeline, which applies it to the frame as a pre-exposure before its
   * histogram ever sees the buffer.
   *
   * Three things changed here and they only make sense together (FINDINGS F1
   * STATUS: NOT CLOSED):
   *
   *  1. **The anchor is local.** It used to be `sky.derived.exposureUnits`, one
   *     number for the whole level, derived outdoors at TOD.golden. Measured,
   *     the exposure the pipeline actually wanted ranged over 4.15 stops between
   *     `level_rooftop` (0.26) and `level_alley` (4.64) — no static anchor can
   *     sit inside a +/-1.5 stop clamp of both, and four of six shots were railed
   *     against that clamp in both directions at once.
   *  2. **The anchor is applied, not just clamped with.** While it was only the
   *     centre of the clamp it could not move the image at all, so a local
   *     anchor would have changed nothing except which shots railed. Now the
   *     frame is pre-exposed by it and the histogram measures the *residual*,
   *     which is what "the multiplier the pipeline settles on" was always meant
   *     to mean.
   *  3. **The display key follows the scene, weakly.** `displayKeyFor` prints a
   *     dark scene darker (EXPOSURE_ADAPT), so a room still reads as a room
   *     instead of being normalised to the same mid-grey as the street. Without
   *     it an unclamped auto-exposure flattens every shot to one key, which is
   *     the other way to lose a frame's value structure.
   *
   * Note the residual the tool reads is `predicted / actual` and is independent
   * of the display-key policy: retuning EXPOSURE_ADAPT changes the picture and
   * cannot change the acceptance number. That separation is on purpose.
   */
  function applyExposure(ctx, dt) {
    const sky = ctx.sky;
    if (!sky) return;
    const ref = sky.derived.keyLuminanceRef;
    if (!ref || !isFinite(ref)) return;

    const key = localKeyLuminance(ctx);
    if (!isFinite(key) || key <= 0) return;

    // Snap on a camera jump (shot change, teleport, spawn): easing across 60 m
    // of level would photograph half the shots mid-adaptation.
    const jumped = _lastExpCam.distanceToSquared(ctx.camera.position) > 2.25;
    _lastExpCam.copy(ctx.camera.position);
    const deterministic = !!(ctx.engine && ctx.engine.deterministic);
    if (keySmoothed <= 0 || jumped || deterministic) {
      keySmoothed = key;
    } else {
      const a = 1 - Math.exp(-Math.max(0, dt || 0) / EXPOSURE_TAU);
      keySmoothed += (key - keySmoothed) * a;
    }

    const displayKey = displayKeyFor(keySmoothed / UNIT_SCALE, ref / UNIT_SCALE);
    const anchor = displayKey / keySmoothed;
    if (!isFinite(anchor) || anchor <= 0) return;

    api.suggestedExposure = anchor;
    api.exposureKey = displayKey;
    api.localKeyLuminance = keySmoothed;
    api.localKeyNits = keySmoothed / UNIT_SCALE;

    if (anchor === exposureApplied) return;
    exposureApplied = anchor;
    if (ctx.render?.setExposure) ctx.render.setExposure(anchor, displayKey);
    else ctx.renderer.toneMappingExposure = anchor;
  }

  /* ---------------- shots ---------------- */

  const _stageDir = new THREE.Vector3();

  /**
   * Park the camera for a shot.
   *
   * `look()` alone is not enough once the real player system is in: the camera
   * rig rewrites camera.position / quaternion / fov every frame from the
   * player's eye, so a shot that only touches ctx.camera ends up wherever the
   * player happens to have fallen to. Freeze the rig and hand it the pose.
   */
  function stage(ctx, from, at, fov) {
    look(ctx, from, at, fov);
    const sys = ctx.get('player');
    const rig = sys && sys.rig;
    if (rig) {
      sys.freeze?.(true);
      _stageDir.set(at[0] - from[0], at[1] - from[1], at[2] - from[2]).normalize();
      rig.reset?.(Math.atan2(-_stageDir.x, -_stageDir.z), Math.asin(THREE.MathUtils.clamp(_stageDir.y, -1, 1)));
      rig.eye.copy(ctx.camera.position);
      rig.quaternion.copy(ctx.camera.quaternion);
      rig.fov = fov;
      rig.applyTo(ctx);
    }
    if (ctx.player) {
      ctx.player.position.copy(ctx.camera.position);
      ctx.player.ads = 0;
    }
    if (ctx.weapons) ctx.weapons.ads = 0;

    // These shots are about the light, not the gun. Hidden only for the dozen
    // settle frames the harness takes before the screenshot, then restored, so
    // shots owned by other teams are unaffected.
    const vm = ctx.weapons && ctx.weapons.viewmodelRoot;
    if (vm) { vm.visible = false; }

    // Exposure stays on the anchor + histogram residual (see applyExposure).
    // `grading.exposureBias` only overrides when a shot needs a deliberate stop
    // up or down; it multiplies the anchor, so the residual absorbs none of it.
    if (grading.exposureBias > 0 && ctx.render && ctx.render.setExposure) {
      ctx.render.setExposure(api.suggestedExposure * grading.exposureBias, api.exposureKey);
      exposureApplied = -1;
    }
    shotStateUntil = frames + 20;
  }

  function levelBounds(ctx) {
    const b = new THREE.Box3();
    if (ctx.level?.navBounds && !ctx.level.navBounds.isEmpty()) b.copy(ctx.level.navBounds);
    else if (ctx.level?.bounds && !ctx.level.bounds.isEmpty()) b.copy(ctx.level.bounds);
    else if (ctx.level?.root) b.setFromObject(ctx.level.root);
    else b.set(new THREE.Vector3(-20, 0, -20), new THREE.Vector3(20, 6, 20));
    return b;
  }

  /**
   * The best interior to shoot: a room hint with a real opening toward the sun.
   * Sorted by radius so we land in the main space, not a stairwell.
   */
  function interiorSite(ctx, wantSunOpening = false, wantEnclosed = false) {
    const hints = (ctx.level?.lightHints || []).filter(
      (h) => h && h.position && PROBE_TYPES.includes((h.type || '').toLowerCase()));
    hints.sort((a, b) => (b.radius || 0) - (a.radius || 0));

    if (hints.length) {
      if (wantEnclosed) {
        // Fewest escaping rays wins: that is the room where a 100 cd bulb is
        // actually the key light rather than a decoration next to daylight.
        let best = null;
        let bestOpen = Infinity;
        for (const h of hints) {
          const p = new THREE.Vector3().copy(h.position);
          let open = 0;
          for (let i = 0; i < 10; i++) {
            const a = (i / 10) * Math.PI * 2;
            _vdir.set(Math.cos(a), 0.12, Math.sin(a)).normalize();
            if (rayDistance(ctx, p, _vdir, 22) === null) open++;
          }
          if (rayDistance(ctx, p, ctx.sky.sunDirection, 40) === null) open += 3;
          if (open < bestOpen) { bestOpen = open; best = p; }
        }
        if (best) return best;
      }
      if (wantSunOpening) {
        const s = ctx.sky.sunDirection;
        for (const h of hints) {
          const p = new THREE.Vector3().copy(h.position);
          const d = rayDistance(ctx, p, s, 40);
          if (d === null || d > 6) return p;   // sky or a distant wall = an opening
        }
      }
      return new THREE.Vector3().copy(hints[0].position);
    }

    const practical = fixtures.find((f) => f.fill) || fixtures.find((f) => f.type !== 'streetlight');
    if (practical) return practical.light.position.clone().add(new THREE.Vector3(0, -0.9, 0));
    return null;
  }

  /**
   * How enclosed a point is. `light_interior` was staging itself by picking the
   * largest room hint and backing off along a direction, with nothing checking
   * that the result was still inside the building — it ended up under a roof
   * slab looking at its underside, and the shot came out 72 % below sRGB 0.10.
   * Everything the shot needs to know is here, measured, not assumed.
   */
  function enclosure(ctx, p) {
    const ceiling = rayDistance(ctx, p, _up, 7);
    let walls = 0;
    let openings = 0;
    let nearest = Infinity;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      _vdir.set(Math.cos(a), 0.02, Math.sin(a)).normalize();
      const d = rayDistance(ctx, p, _vdir, 11);
      if (d === null) openings++;
      else { walls++; nearest = Math.min(nearest, d); }
    }
    const floor = rayDistance(ctx, p, _down, 4);
    return { ceiling, walls, openings, nearest, floor };
  }

  /** Enough clearance around `p` that a camera there is not inside geometry. */
  function clear(ctx, p, r = 0.55) {
    for (const d of CLEAR_DIRS) {
      if (rayDistance(ctx, p, d, r) !== null) return false;
    }
    return true;
  }

  const _up = new THREE.Vector3(0, 1, 0);
  const CLEAR_DIRS = [
    new THREE.Vector3(1, 0, 0), new THREE.Vector3(-1, 0, 0),
    new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 0, -1),
    new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, -1, 0),
  ];

  /** Distance to the first solid surface along `dir`, or null if nothing. */
  function rayDistance(ctx, origin, dir, far = 40) {
    const targets = ctx.level?.collision?.length ? ctx.level.collision : (ctx.level?.root ? [ctx.level.root] : null);
    if (!targets) return null;
    _raycaster.set(origin, dir);
    _raycaster.near = 0.05;
    _raycaster.far = far;
    const hits = _raycaster.intersectObjects(targets, true);
    for (const h of hits) {
      if (h.object.userData.aaaHelper) continue;
      return h.distance;
    }
    return null;
  }

  /**
   * Pick a small prop near the player spawn — the subject of the contact shot.
   * Props are instanced, so the per-object bounding box covers every instance
   * and is useless; walk the instance matrices instead.
   */
  function contactProp(ctx) {
    const anchor = ctx.level?.spawnPoints?.[0]
      ? new THREE.Vector3().copy(ctx.level.spawnPoints[0])
      : levelBounds(ctx).getCenter(new THREE.Vector3());
    const FOLIAGE = /foliage|palm|leaf|tree|plant|vine/i;

    let best = null;
    let bestScore = Infinity;

    const m = new THREE.Matrix4();
    const box = new THREE.Box3();
    const size = new THREE.Vector3();
    const pos = new THREE.Vector3();

    const sun = ctx.sky ? ctx.sky.sunDirection : null;
    const _lit = new THREE.Vector3();

    const consider = (worldBox) => {
      worldBox.getSize(size);
      if (size.y < 0.3 || size.y > 2.4) return;
      if (size.x > 2.6 || size.z > 2.6) return;
      worldBox.getCenter(pos);
      const d = pos.distanceTo(anchor);
      if (d > 55 || d > bestScore) return;
      // Must be sitting on the floor near the player, not perched on a roof:
      // the whole point of the shot is the contact between prop and ground.
      const floor = groundAt(ctx, pos.x, pos.z, worldBox.max.y + 3);
      if (floor === null || Math.abs(floor - worldBox.min.y) > 0.45) return;
      if (Math.abs(floor - (anchor.y - 1.65)) > 2.5) return;

      // A prop in shadow has no contact shadow to show. Check the sun reaches it.
      if (sun) {
        _lit.copy(pos).setY(worldBox.max.y + 0.15);
        if (rayDistance(ctx, _lit, sun, 60) !== null) return;
      }
      bestScore = d;
      best = { pos: pos.clone(), size: size.clone(), min: worldBox.min.clone() };
    };

    ctx.level?.root?.traverse((o) => {
      if (o.userData.aaaHelper) return;
      const mname = (o.material && !Array.isArray(o.material) && o.material.name) || o.name || '';
      if (FOLIAGE.test(mname)) return;
      if (o.isInstancedMesh) {
        if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
        const local = o.geometry.boundingBox;
        const n = Math.min(o.count, 400);
        for (let i = 0; i < n; i++) {
          o.getMatrixAt(i, m);
          m.premultiply(o.matrixWorld);
          box.copy(local).applyMatrix4(m);
          consider(box);
        }
      } else if (o.isMesh) {
        box.setFromObject(o);
        if (!box.isEmpty()) consider(box);
      }
    });
    return best;
  }

  const _down = new THREE.Vector3(0, -1, 0);
  const _probe = new THREE.Vector3();
  const _raycaster = new THREE.Raycaster();

  /** World-space floor height under (x,z), or null if there is nothing there. */
  function groundAt(ctx, x, z, fromY = 40) {
    const targets = ctx.level?.collision?.length ? ctx.level.collision : (ctx.level?.root ? [ctx.level.root] : null);
    if (!targets) return null;
    _probe.set(x, fromY, z);
    _raycaster.set(_probe, _down);
    _raycaster.near = 0;
    _raycaster.far = fromY + 60;
    const hits = _raycaster.intersectObjects(targets, true);
    for (const h of hits) {
      if (h.object.userData.aaaHelper) continue;
      return h.point.y;
    }
    return null;
  }

  /** A standing position in the level we can trust: a real spawn point. */
  function anchorPoint(ctx, index = 0) {
    const sp = ctx.level?.spawnPoints;
    if (sp && sp.length) return new THREE.Vector3().copy(sp[Math.min(index, sp.length - 1)]);
    const b = levelBounds(ctx);
    const c = b.getCenter(new THREE.Vector3());
    const g = groundAt(ctx, c.x, c.z);
    return new THREE.Vector3(c.x, (g ?? 0) + 1.65, c.z);
  }

  /** Sun azimuth in the XZ plane, radians. */
  function sunAzimuth(ctx) {
    const s = ctx.sky.sunDirection;
    return Math.atan2(s.z, s.x);
  }

  const _vhit = new THREE.Vector3();
  const _vdir = new THREE.Vector3();

  /**
   * Find a standing position and heading whose view lands on sunlit geometry.
   *
   * At an 18 deg sun a street canyon is entirely in shadow, so a camera aimed
   * by geometry alone reliably produces a flat, blue frame. This raycasts from
   * every spawn in a fan of headings, tests whether the surface it lands on is
   * actually reached by the sun, and picks the strongest lit vista.
   */
  function sunlitVista(ctx, { eyeHeight = 1.9, headings = 24, reach = 60, preferLow = true } = {}) {
    const sun = ctx.sky.sunDirection;
    const targets = ctx.level?.collision?.length ? ctx.level.collision : (ctx.level?.root ? [ctx.level.root] : null);
    if (!targets) return null;

    // Player spawns only: they are all at street level, so whatever lit
    // surface we find is an upper facade and the shadowed street floor fills
    // the bottom of the frame with the long shadows we came for.
    const spawns = (ctx.level?.spawnPoints || []).slice();
    if (!spawns.length) spawns.push(...(ctx.level?.enemySpawns || []));
    if (!spawns.length) return null;

    // Street level is the interesting place to stand; rooftops are a fallback.
    let baseY = Infinity;
    for (const sp of spawns) baseY = Math.min(baseY, sp.y);

    const pitches = [-0.05, 0.06, 0.2, 0.36];
    let best = null;
    for (const sp of spawns) {
      const g = groundAt(ctx, sp.x, sp.z);
      const eye = new THREE.Vector3(sp.x, (g !== null ? g : sp.y - 1.65) + eyeHeight, sp.z);
      const height = Math.max(0, sp.y - baseY);
      for (let i = 0; i < headings; i++) {
        const a = (i / headings) * Math.PI * 2;
        for (const pitch of pitches) {
          _vdir.set(Math.cos(a), pitch, Math.sin(a)).normalize();
          _raycaster.set(eye, _vdir);
          _raycaster.near = 1.0;
          _raycaster.far = reach;
          const hits = _raycaster.intersectObjects(targets, true);
          let hit = null;
          for (const h of hits) { if (!h.object.userData.aaaHelper) { hit = h; break; } }
          if (!hit || hit.distance < 12) continue;

          _vhit.copy(hit.point).addScaledVector(sun, 0.25);
          if (rayDistance(ctx, _vhit, sun, 80) !== null) continue;  // that surface is in shadow

          // Close enough to read, far enough that the street leads into it, and
          // shot from as low as we can manage.
          let score = 1 / (1 + Math.abs(hit.distance - 24) * 0.07);
          score *= 1 / (1 + Math.abs(pitch) * 1.4);
          if (preferLow) score *= 1 / (1 + height * 0.22);
          if (!best || score > best.score) {
            best = { score, eye: eye.clone(), heading: a, pitch, target: hit.point.clone(), distance: hit.distance };
          }
        }
      }
    }
    return best;
  }

  function registerShots() {
    const settle = { settle: 10 };

    registerShot('light_goldenhour', (ctx) => {
      ctx.lighting.setTimeOfDay(TOD.golden);
      // Player's-eye wide: shadowed street floor leading into a facade the low
      // sun is actually hitting. Warm key on the far wall, cool sky fill in the
      // near shadow — the separation is the whole point of the hour.
      const v = sunlitVista(ctx, { eyeHeight: 2.0 });
      if (v) {
        stage(ctx, [v.eye.x, v.eye.y, v.eye.z],
          [v.target.x, v.target.y + 1.2, v.target.z], 68);
      } else {
        const b = levelBounds(ctx);
        const c = b.getCenter(new THREE.Vector3());
        const eye = anchorPoint(ctx, 1);
        const g = groundAt(ctx, eye.x, eye.z);
        if (g !== null) eye.y = g + 2.2;
        const heading = Math.atan2(c.z - eye.z, c.x - eye.x);
        stage(ctx, [eye.x, eye.y, eye.z],
          [eye.x + Math.cos(heading) * 45, eye.y + 0.8, eye.z + Math.sin(heading) * 45], 68);
      }
    }, { ...settle, desc: 'golden hour wide, long raking shadows' });

    registerShot('light_shafts', (ctx) => {
      ctx.lighting.setTimeOfDay(TOD.golden);
      const site = interiorSite(ctx, false);
      const s = ctx.sky.sunDirection;
      if (site) {
        // Back into the room and look at the openings the sun comes through:
        // shafts, the pool of light they land in, and the cool bounce around it.
        // Stand off to the side of the shaft rather than behind it: a god ray
        // reads as volume when you see it side-on, not down its axis. Stay in
        // the same room so a doorway does not crop the frame.
        const back = new THREE.Vector3(-s.x, 0, -s.z).normalize();
        const side = new THREE.Vector3(-back.z, 0, back.x);
        const roomBack = rayDistance(ctx, site, back, 8);
        const roomSide = rayDistance(ctx, site, side, 8);
        const roomSideNeg = rayDistance(ctx, site, side.clone().negate(), 8);
        if ((roomSideNeg ?? 99) > (roomSide ?? 99)) side.negate();
        const along = Math.min(2.4, roomBack !== null ? Math.max(0.8, roomBack - 0.9) : 2.4);
        const across = Math.min(3.0, Math.max(roomSide ?? 0, roomSideNeg ?? 0) - 0.9);
        const from = site.clone()
          .addScaledVector(back, along)
          .addScaledVector(side, Math.max(0.8, across));
        const g = groundAt(ctx, from.x, from.z, site.y + 2.5);
        if (g !== null && g > site.y - 4.5) from.y = g + 1.62;
        const aim = site.clone().addScaledVector(back, -8);
        stage(ctx, [from.x, from.y, from.z], [aim.x, from.y + 1.9, aim.z], 76);
      } else {
        const eye = anchorPoint(ctx, 0);
        stage(ctx, [eye.x, eye.y, eye.z], [eye.x + s.x * 12, eye.y + 2.6, eye.z + s.z * 12], 76);
      }
    }, { ...settle, desc: 'interior looking into the sun, god rays' });

    registerShot('light_contact', (ctx) => {
      ctx.lighting.setTimeOfDay(TOD.golden);
      const p = contactProp(ctx);
      const s = ctx.sky.sunDirection;
      if (p) {
        // Stand three-quarters into the sun so the prop shows a lit face, a
        // terminator and a shadow raking away across the ground — the shadow
        // is razor sharp where it touches the base and widens along its length.
        const sx = s.x, sz = s.z;
        const len = Math.hypot(sx, sz) || 1;
        const ux = sx / len, uz = sz / len;
        const px = -uz, pz = ux;                     // perpendicular in XZ
        const d = Math.max(1.35, p.size.length() * 1.5);
        const dx = ux * 0.62 + px * 0.78;
        const dz = uz * 0.62 + pz * 0.78;
        const from = new THREE.Vector3(
          p.pos.x + dx * d,
          p.min.y + p.size.y + 0.42,
          p.pos.z + dz * d,
        );
        stage(ctx, [from.x, from.y, from.z],
          [p.pos.x - ux * p.size.x * 0.6, p.min.y + p.size.y * 0.1, p.pos.z - uz * p.size.z * 0.6], 44);
      } else {
        const eye = anchorPoint(ctx, 0);
        const g = groundAt(ctx, eye.x, eye.z) ?? eye.y - 1.65;
        stage(ctx, [eye.x, g + 0.75, eye.z], [eye.x - s.x * 3, g + 0.05, eye.z - s.z * 3], 48);
      }
    }, { ...settle, desc: 'close prop showing contact-hardening shadow' });

    registerShot('light_interior', (ctx) => {
      ctx.lighting.setTimeOfDay(TOD.golden);
      const s = ctx.sky.sunDirection;

      /**
       * Re-authored. The old version trusted `interiorSite(…, wantEnclosed)`
       * and then backed the camera off with no test that it was still in the
       * room; it parked under a roof slab and the shot was 72.4 % below
       * sRGB 0.10 with no subject in it at all. This one measures every
       * candidate room, requires a ceiling over the camera and walls around it,
       * and requires a practical to point at — because that is what the shot is
       * for: a tungsten key against the cool skylight coming through the
       * opening behind it.
       */
      // Same room `light_shafts` uses — the biggest published interior probe
      // site, which is demonstrably a real enclosed hall. The old "most
      // enclosed" search picked somewhere else entirely and put the camera
      // under a roof slab.
      const site = interiorSite(ctx, false);
      if (site) {
        const toSun = new THREE.Vector3(s.x, 0, s.z).normalize();
        // Stand near the window wall (the side the sun comes from) and look the
        // other way into the room: the tungsten practicals and the warm bounce
        // carry the frame, with cool skylight raking in from behind the camera.
        const from = site.clone();
        const roomToSun = rayDistance(ctx, site, toSun, 9);
        if (roomToSun !== null && roomToSun > 2.2) from.addScaledVector(toSun, roomToSun - 1.8);
        const g = groundAt(ctx, from.x, from.z, site.y + 3.0);
        if (g !== null && g > site.y - 4.5) from.y = g + 1.6;
        if (!clear(ctx, from, 0.45)) from.copy(site);

        // Subject: the nearest real fixture in the half we are facing, else the
        // far wall at head height.
        const lamp = fixtures
          .filter((f) => !f.fill && f.light.position.distanceTo(from) < 16
            && f.light.position.clone().sub(from).setY(0).normalize().dot(toSun) < -0.15)
          .sort((a, b) => a.light.position.distanceTo(from) - b.light.position.distanceTo(from))[0];
        const target = lamp
          ? lamp.light.position.clone().setY(lamp.light.position.y - 0.8)
          : from.clone().addScaledVector(toSun, -10).setY(from.y + 0.25);
        stage(ctx, [from.x, from.y, from.z], [target.x, target.y, target.z], 70);
      } else {
        const eye = anchorPoint(ctx, 0);
        const f = fixtures.find((x) => !x.fill) || fixtures[0];
        const t = f ? f.light.position : new THREE.Vector3(eye.x + 4, eye.y, eye.z);
        stage(ctx, [eye.x, eye.y, eye.z], [t.x, t.y - 0.6, t.z], 70);
      }
    }, { ...settle, desc: 'interior lit by practicals, warm/cool separation' });

    registerShot('light_sky', (ctx) => {
      ctx.lighting.setTimeOfDay(TOD.golden);
      /**
       * Highest vantage we have, pitched up, with the sun *inside the frame*.
       *
       * This used to swing 1.75 rad (100 deg) off the sun azimuth. The
       * horizontal half-FOV here is 56 deg, so the sun was always just outside
       * frame — which is why the review found no sun disc, no lit-side rim and
       * no shadow side: every cloud in shot was seen from 100 deg off-axis,
       * where the phase function is flat and every cloud looks the same. At
       * 0.85 rad (49 deg) the disc lands about four fifths of the way to the
       * right edge and roughly three quarters down, so the frame reads
       * diagonally: aureole and silver-lined edges bottom-right, deep grey
       * cores and shadow sides upper-left. The disc is off-centre on purpose —
       * centred it just blooms the whole frame out.
       */
      const spawns = [].concat(ctx.level?.spawnPoints || []).concat(ctx.level?.enemySpawns || []);
      let eye = anchorPoint(ctx, 0);
      for (const sp of spawns) if (sp.y > eye.y) eye = new THREE.Vector3().copy(sp);
      eye.y += 0.6;
      const a = sunAzimuth(ctx) + 0.85;
      stage(ctx, [eye.x, eye.y, eye.z],
        [eye.x + Math.cos(a) * 24, eye.y + 30, eye.z + Math.sin(a) * 24], 80);
    }, { settle: 12, desc: 'pitched up at the sky and clouds, sun in frame' });

    const todShot = (name, t, desc) => {
      registerShot(name, (ctx) => {
        ctx.lighting.setTimeOfDay(t);
        const b = levelBounds(ctx);
        const c = b.getCenter(new THREE.Vector3());
        const eye = anchorPoint(ctx, 0);
        const g = groundAt(ctx, eye.x, eye.z);
        if (g !== null) eye.y = g + 2.4;
        const heading = Math.atan2(c.z - eye.z, c.x - eye.x);
        stage(ctx, [eye.x, eye.y, eye.z],
          [eye.x + Math.cos(heading) * 40, eye.y + 1.6, eye.z + Math.sin(heading) * 40], 62);
      }, { settle: 10, desc });
    };
    todShot('light_tod_dawn', TOD.dawn, 'time of day: dawn');
    todShot('light_tod_noon', TOD.noon, 'time of day: noon');
    todShot('light_tod_golden', TOD.golden, 'time of day: golden hour');
    todShot('light_tod_dusk', TOD.dusk, 'time of day: dusk');
    todShot('light_tod', TOD.golden, 'alias of light_tod_golden — run the four light_tod_* shots');
  }

  /* ---------------- system ---------------- */

  /**
   * Things that must not appear in a reflection-probe cube.
   *
   * `volumetricComposite` is our own additive quad — baking it would feed the
   * shafts back into the IBL.
   *
   * `probe_*` are the render team's viewmodel-lighting rig objects, left in
   * `ctx.scene` (src/render/ProbeRig.js). One of them, `probe_neon`, is an
   * emissive #2fb9ff quad at intensity 14; it is the bright blue rectangle the
   * review found in the `light_shafts` ceiling, and left visible it also bakes a
   * cyan patch into every probe cube. Hiding it for the duration of the bake is
   * local and non-destructive — the object itself belongs to render.
   */
  function hideForBake() {
    const hidden = [];
    ctxRef.scene.traverse((o) => {
      if (!o.visible) return;
      const bakeHelper = o.userData.aaaHelper && o.name === 'volumetricComposite';
      const renderProbeRig = typeof o.name === 'string' && o.name.startsWith('probe_');
      if (bakeHelper || renderProbeRig) { o.visible = false; hidden.push(o); }
    });
    return () => { for (const o of hidden) o.visible = true; };
  }

  const api = {
    // The world rig. These three ARE the scene's absolute lighting scale, and
    // anything that lights the viewmodel must be derived from them — see
    // CONTRACTS.md :: Scene units. `skyFill` and `bounce` were previously
    // private, so consumers fell back to guessed fractions of the sun and the
    // viewmodel drifted ~100x off the world.
    sun: null,
    skyFill: null,
    bounce: null,
    csm: null,
    lights,
    fixtures,
    volumetricTexture: null,
    volumetricScale: volScale,
    volumetricIntensity: 1.0,
    volumetricHasTransmittance: true,
    /**
     * The exposure anchor: the multiplier the frame is pre-exposed by, derived
     * from the lighting model at the camera. NOT the settled exposure — the
     * post pipeline's histogram applies a residual on top of this, and that
     * residual is what `tools/exposure-check.mjs` reads. Reading this value and
     * calling it the exposure is the mistake that invalidated an earlier
     * acceptance test (it is the same number in every shot only when the camera
     * has not moved).
     */
    suggestedExposure: 1.0,
    /** The linear key the pre-exposed frame should meter to (AE target). */
    exposureKey: EXPOSURE_KEY,
    /** Predicted metered luminance at the camera, scene units / nits. */
    localKeyLuminance: 0,
    localKeyNits: 0,
    quality,
    grading,
    /** Unit-luminance chromaticity grade applied to every sky-derived ambient. */
    skyTint,
    env: null,
    diagnostics: null,

    /** Set by the render team once PostFX composites the shafts itself. */
    selfComposite: true,

    /** The material-injection uniform block (diagnostics / ablation / tuning). */
    injectUniforms: sharedUniforms,

    /** K most-present practicals stay lit, the rest are hidden. Infinity = all. */
    setLampBudget(k) {
      lampBudget = k ?? Infinity;
      lampClock = 0;
      if (ctxRef) applyLampBudget(ctxRef);
    },

    setTimeOfDay(t) {
      const before = ctxRef.sky.state.timeOfDay;
      ctxRef.sky.setTimeOfDay(t);
      if (ctxRef.sky.state.timeOfDay !== before) {
        syncRig(ctxRef);
        envDirty = true;
      }
    },

    setQuality(preset) {
      if (preset === 'low') {
        quality.volumetrics = false;
        quality.volumetricSteps = 24;
        quality.shadowCasterBudget = 1;
      } else if (preset === 'medium') {
        quality.volumetrics = true;
        quality.volumetricSteps = 28;
        quality.shadowCasterBudget = 2;
      } else {
        quality.volumetrics = true;
        quality.volumetricSteps = 40;
        quality.shadowCasterBudget = 3;
      }
    },

    rebuildPracticals() { buildPracticals(ctxRef); },
    rebakeEnvironment() { envDirty = true; },
    registerMaterial,
    update: null,
  };

  return {
    name: 'lighting',
    priority: 35,

    init(ctx) {
      ctxRef = ctx;

      ctx.renderer.shadowMap.enabled = true;
      ctx.renderer.shadowMap.type = THREE.PCFShadowMap;   // VSM leaks and acnes badly
      ctx.renderer.shadowMap.autoUpdate = false;          // practicals are static
      ctx.renderer.shadowMap.needsUpdate = true;

      const tile = ctx.quality === 'low' ? 512 : ctx.quality === 'medium' ? 768 : 1024;
      csm = new CascadeAtlas(ctx.renderer, { tile });

      const b = levelBounds(ctx);
      const diag = b.getSize(new THREE.Vector3()).length();
      csm.far = THREE.MathUtils.clamp(diag * 1.15, 55, 260);
      csm.margin = THREE.MathUtils.clamp(diag * 0.6, 40, 120);

      sharedUniforms.aaaShadowParams.value.set(2, tile, 0.019, 1.25);
      sharedUniforms.aaaShadowBias.value.set(0.018, 0.11, 1.5, 1.0);

      buildRig(ctx);
      syncRig(ctx);
      buildPracticals(ctx);
      buildVolumetrics(ctx);

      registerSceneMaterials(ctx.scene);

      env = createEnv();
      env.init(ctx);

      api.sun = sun;
      api.skyFill = skyFill;
      api.bounce = bounce;
      api.csm = csm;
      api.env = env;
      api.volumetricTexture = volRT.texture;
      api.volumetricScale = volScale;
      // Exposed so the shafts can be tuned and ablated from the console and by
      // the render team without reaching into this module.
      api.volumetricUniforms = volMat.uniforms;
      api.diagnostics = { shaderPatches: patchReport, cascades: CASCADE_COUNT, tile };
      api.uniforms = sharedUniforms;
      if (typeof window !== 'undefined') window.__AAA_UNIFORMS__ = sharedUniforms;
      api.update = (dt) => this.update(dt, ctx);

      ctx.lighting = api;
      ctx.env = env;      // IBL surface: probes, irradiance volume, rebake hooks

      registerShots();
    },

    update(dt, ctx) {
      frames++;
      const sky = ctx.sky;
      if (!sky) return;

      syncRig(ctx);
      applyExposure(ctx, dt);

      if (Number.isFinite(lampBudget)) {
        lampClock += dt;
        if (lampClock >= 0.75) { lampClock = 0; applyLampBudget(ctx); }
      }

      if (shotStateUntil >= 0 && frames <= shotStateUntil) {
        // Re-assert it every frame. Setting it once in stage() is not enough —
        // the weapons system re-shows its own root on its own schedule, and the
        // gun turned up occupying 40 % of `light_shafts`, which is a shot about
        // a room.
        const vm = ctx.weapons && ctx.weapons.viewmodelRoot;
        if (vm) vm.visible = false;
      }

      if (shotStateUntil >= 0 && frames > shotStateUntil) {
        shotStateUntil = -1;
        const vm = ctx.weapons && ctx.weapons.viewmodelRoot;
        if (vm) vm.visible = true;
        // Restore the physically derived anchor rather than clearing it —
        // setExposure(0) used to drop the post pipeline back to an unclamped
        // window, which is precisely the state F1 was hiding in.
        exposureApplied = -1;
      }

      if (frames % 45 === 1) registerSceneMaterials(ctx.scene);

      // Decide once whether the level bothered to flag its shadow casters.
      if (frames === 1) {
        // If the level never flagged its casters, treat everything as one —
        // missing shadows are far worse than a slightly fatter shadow pass.
        // Only the level's own tree counts; other teams' debug rigs must not
        // make us think the flags are authored.
        let casters = 0;
        let meshes = 0;
        (ctx.level?.root || ctx.scene).traverse((o) => {
          if (!o.isMesh) return;
          meshes++;
          if (o.castShadow === true) casters++;
        });
        allCasters = casters < Math.max(1, meshes * 0.1);
        if (ctx.level?.lightHints?.length && fixtures.length === 0) buildPracticals(ctx);
      }

      if (quality.shadows) {
        csm.update(ctx.camera, sky.sunDirection);
        csm.render(ctx.renderer, ctx.scene, allCasters);
      } else {
        sharedUniforms.aaaShadowBias.value.w = 0;
      }

      // Environment bakes wait a frame so the shadow atlas and practicals exist.
      if (envDirty && frames >= 2) {
        envDirty = false;
        env.refresh({
          hideForBake,
          bakeProbes: quality.probes,
          bakeVolume: quality.irradianceVolume,
        });
        ctx.renderer.shadowMap.needsUpdate = true;
      }

      renderVolumetrics(ctx);

      if (compositeQuad) {
        /**
         * `api.selfComposite` is the documented hand-off switch (CONTRACTS.md:
         * "Set by the render team once PostFX composites the shafts itself").
         * This used to *also* disable itself whenever a pass named `volumetric`
         * merely existed in `ctx.render.passes`. It does exist — and measured,
         * it delivers essentially nothing: bypassing it and letting this quad
         * composite instead moves the `light_shafts` beam region by +0.038 mean
         * luminance where the render pass moves it by ~0.000. So the shafts were
         * being computed, published, and then dropped on the floor.
         *
         * Honour the flag and nothing else. Render owns the hand-off by setting
         * `ctx.lighting.selfComposite = false`; until then lighting composites
         * its own shafts. (Additive only — transmittance is 0.9-1.0 here, and
         * multiplying it in from an un-gated buffer is exactly the F1 accident.)
         */
        compositeQuad.visible = api.selfComposite && quality.volumetrics;
        compositeMat.uniforms.uIntensity.value = grading.volumetricIntensity;
        api.volumetricIntensity = grading.volumetricIntensity;
      }
    },

    resize(w, h, ctx) {
      if (volRT) resizeVolumetrics(ctx);
    },

    dispose() {
      csm?.dispose();
      volRT?.dispose();
      volDepthRT?.dispose();
      volMat?.dispose();
      compositeMat?.dispose();
      compositeQuad?.removeFromParent();
      env?.dispose();
      for (const l of lights) l.removeFromParent?.();
    },
  };
}
